import "./MarkPreparationReadyAction.css";

import {
  AlertTriangle,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";

import {
  useState,
} from "react";

import {
  markContentCalendarPreparationReady,
  type ContentCalendarItem,
} from "../lib/contentCalendar";

interface MarkPreparationReadyActionProps {
  calendarItem:
    ContentCalendarItem;

  onUpdated:
    (
      calendarItem:
        ContentCalendarItem,
    ) => void;
}

function MarkPreparationReadyAction({
  calendarItem,
  onUpdated,
}: MarkPreparationReadyActionProps) {
  const [
    isConfirming,
    setIsConfirming,
  ] =
    useState(
      false,
    );

  const [
    notes,
    setNotes,
  ] =
    useState(
      "",
    );

  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(
      false,
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  if (
    calendarItem.status !==
      "planned"
  ) {
    return null;
  }

  if (
    !isConfirming
  ) {
    return (
      <section className="calendar-workflow-action">
        <div className="calendar-workflow-action-copy">
          <span className="calendar-workflow-icon">
            <ShieldCheck
              aria-hidden
              size={18}
              strokeWidth={1.8}
            />
          </span>

          <div>
            <h3>
              Preparation workflow
            </h3>

            <p>
              Mark this item ready after its research, editorial
              requirements and production brief have been reviewed.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="primary-button"
          onClick={() => {
            setError(
              null,
            );

            setIsConfirming(
              true,
            );
          }}
        >
          <CheckCircle2
            aria-hidden
            size={16}
            strokeWidth={1.9}
          />

          Mark preparation ready
        </button>
      </section>
    );
  }

  return (
    <section className="calendar-workflow-confirmation">
      <header>
        <span className="calendar-workflow-icon">
          <ShieldCheck
            aria-hidden
            size={18}
            strokeWidth={1.8}
          />
        </span>

        <div>
          <h3>
            Confirm preparation readiness
          </h3>

          <p>
            This changes the production status from
            <strong> Planned </strong>
            to
            <strong> Preparation ready</strong>
            and records an audit event.
          </p>
        </div>
      </header>

      {error ? (
        <div
          className="calendar-workflow-error"
          role="alert"
        >
          <AlertTriangle
            aria-hidden
            size={17}
            strokeWidth={1.9}
          />

          <span>
            {error}
          </span>
        </div>
      ) : null}

      <label className="calendar-workflow-notes">
        <span>
          Audit notes
        </span>

        <textarea
          rows={4}
          maxLength={3_000}
          value={notes}
          disabled={isSubmitting}
          placeholder="Optional: summarize the research, editorial and production checks that are complete."
          onChange={(
            event,
          ) => {
            setNotes(
              event.target.value,
            );
          }}
        />

        <small>
          {notes.length}/3000
        </small>
      </label>

      <footer>
        <span>
          This is a production workflow change.
        </span>

        <div>
          <button
            type="button"
            className="secondary-button"
            disabled={isSubmitting}
            onClick={() => {
              setError(
                null,
              );

              setNotes(
                "",
              );

              setIsConfirming(
                false,
              );
            }}
          >
            Cancel
          </button>

          <button
            type="button"
            className="primary-button"
            disabled={isSubmitting}
            onClick={async () => {
              setError(
                null,
              );

              setIsSubmitting(
                true,
              );

              try {
                const response =
                  await markContentCalendarPreparationReady({
                    calendarItemId:
                      calendarItem.id,

                    notes,
                  });

                setNotes(
                  "",
                );

                setIsConfirming(
                  false,
                );

                onUpdated(
                  response.calendarItem,
                );
              } catch (
                transitionError
              ) {
                setError(
                  transitionError instanceof
                    Error
                    ? transitionError.message
                    : "The preparation-ready transition could not be completed.",
                );
              } finally {
                setIsSubmitting(
                  false,
                );
              }
            }}
          >
            <CheckCircle2
              aria-hidden
              size={16}
              strokeWidth={1.9}
            />

            {isSubmitting
              ? "Updating..."
              : "Confirm preparation ready"}
          </button>
        </div>
      </footer>
    </section>
  );
}

export default MarkPreparationReadyAction;
